"use client";

import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { useListPagination } from "@/hooks";
import { X } from "lucide-react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type { PublisherEventFeedItem } from "../types";
import type { EventType } from "../config/eventTypes";
import { publisherEventsSkeletonConfig } from "../config";
import { PublisherEventsList } from "./PublisherEventsList";

interface PublisherEventsListWithPaginationProps {
  initialEvents: PublisherEventFeedItem[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  clanid: number;
  eventType: EventType;
  gameFilter?: number | null;
  filteredGameName?: string | null;
}

async function loadPublisherEvents(
  clanid: number,
  page: number,
  pageSize: number,
  eventType?: EventType,
  gameFilter?: number | null,
): Promise<{ items: PublisherEventFeedItem[]; total: number }> {
  const { getPublisherEventsClient } =
    await import("../api/getPublisherEventsClient");
  const offset = (page - 1) * pageSize;
  const { events, total } = await getPublisherEventsClient(
    clanid,
    pageSize,
    offset,
    undefined,
    eventType,
    gameFilter,
  );
  return { items: events, total };
}

export function PublisherEventsListWithPagination({
  initialEvents,
  initialPage,
  initialTotal,
  pageSize,
  clanid,
  eventType,
  gameFilter,
  filteredGameName,
}: PublisherEventsListWithPaginationProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const {
    items: events,
    currentPage,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handlePageSizeChange,
  } = useListPagination({
    initialItems: initialEvents,
    initialPage,
    initialTotal,
    pageSize,
    sortable: false,
    onPageChange: (page, pageSize) =>
      loadPublisherEvents(clanid, page, pageSize, eventType, gameFilter),
  });

  const handleClearGameFilter = () => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.delete("game_filter");
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  return (
    <div className="space-y-6">
      {gameFilter && filteredGameName && (
        <div className="inline-flex items-center gap-2 bg-[#16202d] rounded-lg border border-[#2a475e] px-3 py-2">
          <span className="text-xs text-[#8f98a0]">Filtered by game:</span>
          <span className="text-sm text-[#66c0f4] font-medium">
            {filteredGameName}
          </span>
          <button
            onClick={handleClearGameFilter}
            className="text-[#8f98a0] hover:text-white transition-colors ml-1"
            title="Clear filter"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {isPending ? (
        <ListSkeleton columns={publisherEventsSkeletonConfig} />
      ) : (
        <PublisherEventsList events={events} />
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={currentPageSize}
        onPageSizeChange={handlePageSizeChange!}
        disabled={isPending}
      />
    </div>
  );
}
